// src/components/modals/EditLogModal/hooks/useEditLogForm.js
import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../../store/slices/authSlice";
import { studentApi, classLogApi } from "../../../../services/api";
import {
	getEditableWeekDays,
	toMidnightUTC,
} from "../../../../utils/dateUtils";

export function useEditLogForm(log, onUpdateSuccess, onClose) {
	const user = useSelector(selectCurrentUser);
	const logData = log?.ClassLog || log;

	const [lectureTitle, setLectureTitle] = useState(
		logData?.lectureTitle || logData?.LectureTitle || ""
	);
	const [sequence, setSequence] = useState(
		(logData?.sequence || logData?.Sequence)?.toString() || "1"
	);
	const [absentStudents, setAbsentStudents] = useState([]);
	const [studentOptions, setStudentOptions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [notification, setNotification] = useState("");
	const [selectedDay, setSelectedDay] = useState("");
	const [period, setPeriod] = useState(
		(logData?.period || logData?.Period)?.toString() || ""
	);

	const departmentId = logData?.departmentId || logData?.DepartmentId;

	// Generate weekdays based on the log's date
	const weekDays = useMemo(() => {
		const dateToUse = logData?.classDate || logData?.ClassDate;
		return getEditableWeekDays(dateToUse);
	}, [logData]);

	// Initialize day based on classDate from log
	useEffect(() => {
		const dateToUse = logData?.classDate || logData?.ClassDate;
		if (dateToUse) {
			try {
				// Parse the date properly, ensuring we use the correct date
				const date = new Date(dateToUse);
				// Format as YYYY-MM-DD in local time, not UTC
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, "0");
				const day = String(date.getDate()).padStart(2, "0");
				const formattedDate = `${year}-${month}-${day}`;
				setSelectedDay(formattedDate);
			} catch (error) {
				console.error("Error formatting date:", error);
				setSelectedDay("");
			}
		}
	}, [logData]);

	// Fetch students (keeping the same)
	useEffect(() => {
		if (!departmentId || !user?.token) return;

		const fetchStudents = async () => {
			try {
				const students = await studentApi.getStudentsByDepartment(
					departmentId,
					user.token
				);

				const formattedStudents = students.map((student) => ({
					value: student.id,
					label: `${student.firstName} ${student.lastName}`,
				}));

				setStudentOptions(formattedStudents);

				// Format absent students to match the dropdown options
				const absentList = log?.absentStudents || log?.AbsentStudents || [];
				if (absentList.length > 0) {
					const formattedAbsent = absentList
						.map((absent) => {
							const studentId = absent.studentId || absent.StudentId;
							return formattedStudents.find(
								(student) => student.value === studentId
							);
						})
						.filter(Boolean);

					setAbsentStudents(formattedAbsent);
				}
			} catch (error) {
				console.error("Error fetching students:", error);
				setNotification("Error fetching students. Please try again.");
			}
		};

		fetchStudents();
	}, [departmentId, log, user?.token]);

	const toggleStudentAbsence = (student) => {
		const isAbsent = absentStudents.some((s) => s.value === student.value);

		if (isAbsent) {
			setAbsentStudents(
				absentStudents.filter((s) => s.value !== student.value)
			);
		} else {
			setAbsentStudents([...absentStudents, student]);
		}
	};

	const validateForm = () => {
		if (!lectureTitle.trim()) {
			setNotification("Please enter a lecture title.");
			return false;
		}

		if (!selectedDay) {
			setNotification("Please select a day of the week.");
			return false;
		}

		if (!period) {
			setNotification("Please select a period.");
			return false;
		}

		return true;
	};

	const handleSubmit = async () => {
		if (!validateForm()) return;

		// Use the midnight UTC conversion when saving
		const utcDate = toMidnightUTC(selectedDay);

		const updatedLog = {
			lectureTitle,
			lectureType: logData?.lectureType || logData?.LectureType || "Lecture",
			classDate: utcDate, // Send UTC midnight date
			period: period,
			sequence: parseInt(sequence, 10) || 1,
			absentStudentIds: absentStudents.map((s) => s.value),
		};

		setIsLoading(true);
		try {
			const logId =
				logData?.classLogId ||
				logData?.ClassLogId ||
				logData?.id ||
				logData?.Id;

			await classLogApi.updateClassLog(logId, updatedLog, user.token);

			// Call the success callback with updated data
			onUpdateSuccess({
				...log,
				...updatedLog,
				absentStudents: absentStudents.map((item) => ({
					studentId: item.value,
					name: item.label,
				})),
			});

			onClose();
		} catch (error) {
			console.error("Error updating log:", error);
			setNotification("Error updating log. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	return {
		lectureTitle,
		setLectureTitle,
		sequence,
		setSequence,
		selectedDay,
		setSelectedDay,
		period,
		setPeriod,
		absentStudents,
		studentOptions,
		weekDays,
		isLoading,
		notification,
		setNotification,
		handleSubmit,
		toggleStudentAbsence,
	};
}
